import { Navigate, Outlet, Route } from "react-router-dom";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import {
  featureFlagRoute,
  makeFeatureFlagRedirectLoader,
} from "@/shared/routing/featureFlagRoute";

import {
  SessionDetail,
  SessionDetailsTab,
  SessionMetricsTab,
  SessionTab,
  SourcesTab,
  TopicsTab,
} from "./SessionDetail";
import { SessionsPage } from "./SessionsPage";

/**
 * Route tree for `/sessions`.
 *
 * - The list layout (`/sessions`) preloads saved table preferences, then
 *   gates render on the `release-sessions` flag and redirects to
 *   `/search` when off.
 * - The detail layout (`/sessions/:sessionId/*`) is a sibling rather
 *   than a child of the list — the detail page owns its own chrome.
 *   `featureFlagRoute` returns a single childless `<Route>`, so the
 *   detail gate uses `makeFeatureFlagRedirectLoader` directly on the
 *   parent route here.
 * - The `metrics` tab is gated separately by `release-metrics` — sessions
 *   can ship before metrics dashboards do.
 *
 * Spread into the top-level route list (`{...SessionsRoutes()}`).
 * The inner `featureFlagRoute` calls already bind the absolute paths.
 */
export const SessionsRoutes = () => [
  <Route
    key="sessions-preferences-layout"
    loader={loadSearchTablePreferences(
      queryClient,
      "session_search_table_preferences",
    )}
    element={<Outlet />}
  >
    {featureFlagRoute({
      path: "/sessions",
      flagName: "release-sessions",
      redirectTo: "/search",
      element: <SessionsPage />,
    })}
  </Route>,
  <Route
    key="session-detail"
    path="/sessions/:sessionId"
    loader={makeFeatureFlagRedirectLoader(queryClient, {
      flagName: "release-sessions",
      redirectTo: "/search",
    })}
    element={<SessionDetail />}
  >
    <Route index element={<Navigate to={SessionTab.Topics} replace />} />
    <Route path={SessionTab.Topics} element={<TopicsTab />} />
    <Route path={SessionTab.Sources} element={<SourcesTab />} />
    {featureFlagRoute({
      path: "/sessions/:sessionId/metrics",
      flagName: "release-metrics",
      redirectTo: ({ params }) =>
        `/sessions/${params.sessionId}/${SessionTab.Topics}`,
      element: <SessionMetricsTab />,
    })}
    <Route path={SessionTab.Details} element={<SessionDetailsTab />} />
    <Route path="*" element={<Navigate to={SessionTab.Topics} replace />} />
  </Route>,
];
